import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalUtilidades.css';
import '../styles/fiscalModerno.css';

// Fiscal → Consulta de NFS-e por chave (port do ZADRA). O operador cola a chave de 50
// dígitos; a tela valida o leiaute e o DV na hora, o Saber sugere a empresa cujo CNPJ é o
// emitente (se for única entre as que têm certificado A1 válido) e a consulta roda na hora
// na Sefin Nacional (só leitura). O XML encontrado é gravado com as demais notas da empresa.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const AMBIENTES = { production: 'Produção', restrictedProduction: 'Produção restrita' };
const STATUS = {
  'Concluído': { cls: 'ok', ico: 'fa-circle-check' },
  Erro: { cls: 'erro', ico: 'fa-circle-xmark' },
  Processando: { cls: 'alerta', ico: 'fa-spinner fa-spin' },
  Pendente: { cls: 'neutro', ico: 'fa-clock' },
};

const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const fmtValor = (v) => (v == null || v === '' ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const chaveCurta = (c) => (c ? `${c.slice(0, 9)}…${c.slice(-8)}` : '');

// mesma validação do servidor (fiscal_consulta_chave.analisar)
const analisarChave = (valor) => {
  const chave = String(valor || '').replace(/\D/g, '');
  if (!chave) return null;
  if (chave.length !== 50) return { valida: false, chave, erro: `A chave deve possuir 50 dígitos (${chave.length} informados).` };
  const ambGer = Number(chave[7]);
  const tipo = Number(chave[8]);
  if (![1, 2].includes(ambGer)) return { valida: false, chave, erro: 'Ambiente gerador inválido na chave.' };
  if (![1, 2].includes(tipo)) return { valida: false, chave, erro: 'Tipo de inscrição inválido na chave.' };
  const mes = Number(chave.substring(38, 40));
  if (mes < 1 || mes > 12) return { valida: false, chave, erro: 'Mês de emissão inválido na chave.' };
  let soma = 0;
  let peso = 2;
  for (let i = 48; i >= 0; i -= 1) {
    soma += Number(chave[i]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  let dv = 11 - (soma % 11);
  if (dv >= 10) dv = 0;
  if (dv !== Number(chave[49])) return { valida: false, chave, erro: 'Dígito verificador inválido.' };
  return {
    valida: true, chave, ambGer, tipo,
    municipio: chave.substring(0, 7),
    emitente: chave.substring(9, 23),
    numero: String(Number(chave.substring(23, 36))),
    aamm: `${chave.substring(38, 40)}/20${chave.substring(36, 38)}`,
  };
};

function SeloStatus({ status }) {
  const s = STATUS[status] || STATUS.Pendente;
  return <span className={`fz-selo ${s.cls}`}><i className={`fas ${s.ico}`}></i>{status || '—'}</span>;
}

function Resultado({ c, onBaixar }) {
  const situacao = c.status !== 'Concluído' ? null
    : c.participacao === 'Intermediário' ? 'Consultada sem gravar (empresa intermediária)'
      : c.ja_existente ? 'Já existia no Saber (mesmo conteúdo)' : 'Baixada e gravada agora';
  return (
    <div className="fz-resultado">
      <div className="fz-resultado-topo">
        <SeloStatus status={c.status} />
        {situacao && <span className="fw-semibold">{situacao}</span>}
        {c.codigo_erro && <span className="fz-selo neutro fz-mono">{c.codigo_erro}</span>}
        {c.documento_id && (
          <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => onBaixar(c)}>
            <i className="fas fa-download me-1"></i>Baixar XML
          </button>
        )}
      </div>
      {c.mensagem && <div className="small mb-2">{c.mensagem}</div>}
      <dl className="fz-chave-campos mb-0">
        <div><dt>Empresa</dt><dd>{c.empresa_cod} · {c.cliente}</dd></div>
        <div><dt>Participação</dt><dd>{c.participacao || '—'}</dd></div>
        <div><dt>Número</dt><dd>{c.numero || '—'}</dd></div>
        <div><dt>Valor do serviço</dt><dd>{fmtValor(c.valor)}</dd></div>
        <div><dt>Prestador</dt><dd>{c.prestador || '—'}</dd></div>
        <div><dt>Tomador</dt><dd>{c.tomador || '—'}</dd></div>
        <div><dt>Ambiente</dt><dd>{AMBIENTES[c.ambiente] || c.ambiente || '—'}</dd></div>
        <div><dt>Chave</dt><dd className="fz-mono small">{c.chave}</dd></div>
      </dl>
    </div>
  );
}

export default function FiscalConsultaChave() {
  const [empresas, setEmpresas] = useState([]);
  const [chave, setChave] = useState('');
  const [empresaCod, setEmpresaCod] = useState('');
  const [origemEmpresa, setOrigemEmpresa] = useState('');   // 'sugerida' | 'manual'
  const [ambiente, setAmbiente] = useState('production');
  const [noSaber, setNoSaber] = useState([]);
  const [consultando, setConsultando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const ultimaAnalise = useRef('');

  const analise = useMemo(() => analisarChave(chave), [chave]);

  const carregarHistorico = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/consulta/historico/`, { params: { _: Date.now(), empresa_cod: filtroEmpresa || undefined }, headers: headers() })
      .then((r) => setHistorico(r.data.consultas || []))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar o histórico'))
      .finally(() => setCarregando(false));
  }, [filtroEmpresa]);
  useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

  useEffect(() => {
    axios.get(`${API}/api/fiscal/consulta/empresas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setEmpresas(r.data.empresas || []))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as empresas'));
  }, []);

  // chave válida -> servidor sugere a empresa e diz se a nota já está no Saber
  useEffect(() => {
    if (!analise?.valida) { setNoSaber([]); ultimaAnalise.current = ''; return; }
    if (ultimaAnalise.current === analise.chave) return;
    ultimaAnalise.current = analise.chave;
    axios.post(`${API}/api/fiscal/consulta/analisar/`, { chave: analise.chave }, { headers: headers() })
      .then((r) => {
        if (ultimaAnalise.current !== analise.chave) return;
        setNoSaber(r.data.no_saber || []);
        const s = r.data.sugestao;
        if (s) { setEmpresaCod(String(s.cod)); setOrigemEmpresa('sugerida'); }
        else setOrigemEmpresa((o) => (o === 'sugerida' ? '' : o));
      })
      .catch(() => setNoSaber([]));
  }, [analise]);

  const consultar = () => {
    setConsultando(true); setErro(''); setResultado(null);
    axios.post(`${API}/api/fiscal/consulta/`, { chave: analise.chave, empresa_cod: Number(empresaCod), ambiente }, { headers: headers() })
      .then((r) => { setResultado(r.data.consulta); carregarHistorico(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível consultar a NFS-e'))
      .finally(() => setConsultando(false));
  };

  const baixarXml = (c) => {
    axios.get(`${API}/api/fiscal/consulta/${c.id}/xml/`, { headers: headers(), responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `NFSE-${c.numero || 'SEM-NUMERO'}-${c.chave}.xml`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => setErro('Não foi possível baixar o XML'));
  };

  const municipal = analise?.valida && analise.ambGer !== 2;
  const podeConsultar = analise?.valida && !municipal && empresaCod && !consultando;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-key header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Consulta de NFS-e por chave</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={carregarHistorico} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Consulta a NFS-e na <b>Sefin Nacional</b> com o certificado A1 da empresa e grava o XML junto das demais notas dela
          (prestadora → emitidas, tomadora → recebidas). Atende só chaves geradas pela Sefin Nacional (<b>ambGer = 2</b>);
          notas de sistema municipal não têm XML por chave na API.
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}

        <section className="po-card">
          <header className="po-card-head"><h3><i className="fas fa-magnifying-glass" aria-hidden="true"></i>Nova consulta</h3></header>
          <div className="po-card-body">
            <label className="form-label small fw-bold mb-1" htmlFor="fz-chave">Chave de acesso (50 dígitos)</label>
            <input id="fz-chave" className="form-control fz-chave-input" autoComplete="off" spellCheck={false}
              placeholder="Cole a chave da NFS-e" value={chave}
              onChange={(ev) => { setChave(ev.target.value); setResultado(null); }} />
            {analise && !analise.valida && <div className="small text-danger mt-1"><i className="fas fa-circle-xmark me-1"></i>{analise.erro}</div>}
            {analise?.valida && (
              <>
                <div className={`small mt-1 ${municipal ? 'text-danger' : 'text-success'}`}>
                  <i className={`fas ${municipal ? 'fa-ban' : 'fa-circle-check'} me-1`}></i>
                  {municipal
                    ? 'Chave gerada por sistema municipal (ambGer = 1): a Sefin Nacional não devolve o XML por chave.'
                    : 'Chave válida (dígito verificador confere).'}
                </div>
                <dl className="fz-chave-campos">
                  <div><dt>Município (IBGE)</dt><dd className="fz-mono">{analise.municipio}</dd></div>
                  <div><dt>Emitente</dt><dd className="fz-mono">{analise.tipo === 2 ? fmtCnpj(analise.emitente) : 'CPF'}</dd></div>
                  <div><dt>Número</dt><dd>{analise.numero}</dd></div>
                  <div><dt>Emissão</dt><dd>{analise.aamm}</dd></div>
                </dl>
                {noSaber.length > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="fas fa-database me-1"></i>Esta nota já está no Saber: {noSaber.map((n) => `${n.empresa_cod} · ${n.cliente || ''} (${n.tipo})`).join('; ')}
                  </div>
                )}
              </>
            )}

            <div className="row g-2 mt-2">
              <div className="col-md-8">
                <label className="form-label small fw-bold mb-1" htmlFor="fz-empresa">Empresa (certificado A1 válido)</label>
                <select id="fz-empresa" className="form-select" value={empresaCod}
                  onChange={(ev) => { setEmpresaCod(ev.target.value); setOrigemEmpresa(ev.target.value ? 'manual' : ''); }}>
                  <option value="">{empresas.length ? 'Selecione uma empresa' : 'Nenhuma empresa com certificado A1 válido'}</option>
                  {empresas.map((e) => <option key={e.cod} value={e.cod}>{e.cliente} ({e.cod}) — {fmtCnpj(e.cnpj)}</option>)}
                </select>
                <div className="fz-sub mt-1">
                  {origemEmpresa === 'sugerida' && 'Empresa identificada pelo CNPJ do emitente da chave. Você pode trocar.'}
                  {origemEmpresa === 'manual' && 'Empresa selecionada manualmente. A participação é confirmada pelo XML.'}
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1" htmlFor="fz-ambiente">Ambiente</label>
                <select id="fz-ambiente" className="form-select" value={ambiente} onChange={(ev) => setAmbiente(ev.target.value)}>
                  {Object.entries(AMBIENTES).map(([k, rot]) => <option key={k} value={k}>{rot}</option>)}
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button type="button" className="btn btn-primary" onClick={consultar} disabled={!podeConsultar}>
                <i className={`fas ${consultando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'} me-2`}></i>
                {consultando ? 'Consultando a Sefin Nacional...' : 'Consultar e baixar'}
              </button>
            </div>
            {resultado && <div className="mt-3"><Resultado c={resultado} onBaixar={baixarXml} /></div>}
          </div>
        </section>

        <section className="po-card mt-3">
          <header className="po-card-head">
            <h3><i className="fas fa-clock-rotate-left" aria-hidden="true"></i>Histórico de consultas</h3>
            <select className="fz-sel ms-auto" value={filtroEmpresa} onChange={(ev) => setFiltroEmpresa(ev.target.value)} aria-label="Filtrar por empresa">
              <option value="">Todas as empresas</option>
              {empresas.map((e) => <option key={e.cod} value={e.cod}>{e.cliente}</option>)}
            </select>
          </header>
          <div className="po-card-body">
            <div className="po-scroll" style={{ maxHeight: 'calc(100vh - 360px)', minHeight: 200 }}>
              <table className="table table-hover po-table align-middle mb-0">
                <thead><tr><th>Quando</th><th>Empresa</th><th>Nota</th><th>Participação</th><th>Valor</th><th>Situação</th><th></th></tr></thead>
                <tbody>
                  {(historico || []).map((c) => (
                    <tr key={c.id}>
                      <td className="small fz-num">{c.criado_em || c.concluido_em || '—'}<div className="fz-sub">{c.solicitado_por || ''}</div></td>
                      <td><div className="fw-semibold">{c.cliente}</div><div className="fz-sub fz-mono">{c.empresa_cod}</div></td>
                      <td><div>{c.numero ? `Nº ${c.numero}` : '—'}</div><div className="fz-chave-curta" title={c.chave}>{chaveCurta(c.chave)}</div></td>
                      <td className="small">{c.participacao || '—'}</td>
                      <td className="fz-num">{fmtValor(c.valor)}</td>
                      <td>
                        <SeloStatus status={c.status} />
                        {c.status === 'Concluído' && c.ja_existente && <div className="fz-sub">já existia</div>}
                        {c.status === 'Erro' && <div className="fz-sub" title={c.mensagem || ''}>{c.codigo_erro || 'erro'}</div>}
                      </td>
                      <td className="text-end">
                        {c.documento_id && (
                          <button type="button" className="btn btn-sm btn-link p-0" title="Baixar XML" onClick={() => baixarXml(c)}>
                            <i className="fas fa-download"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {historico && historico.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Nenhuma consulta ainda.</td></tr>}
                  {!historico && <tr><td colSpan={7} className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Carregando...</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
